import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { JwtAuthService } from 'app/shared/services/auth/jwt-auth.service';

/**
 * Imagem servida por endpoint autenticado, para uso em `[src]`.
 *
 * <p>O endpoint exige `Authorization`, e a tag `<img>` não manda cabeçalho — daí o XHR. O que o
 * pipe faz de diferente agora:</p>
 *
 * - **Object URL no lugar de base64.** O data URL inflava a imagem em ~33%, exigia uma passada do
 *   FileReader por imagem e deixava a string inteira no DOM. O object URL é uma referência.
 *   Ele PRECISA passar pelo DomSanitizer: `blob:` não está entre os esquemas que o Angular aceita
 *   em `[src]`, e sem isso a URL vira `unsafe:blob:...` e a imagem simplesmente não aparece —
 *   diferente do `data:`, que tem regra própria no sanitizador e por isso funcionava antes.
 * - **Memória entre chamadas.** A grade de produtos da comanda pede a mesma imagem a cada rolagem
 *   e a cada reabertura do diálogo; sem memória, cada volta refazia requisição e conversão.
 *
 * <p>O cache é por sessão do navegador, com teto e descarte do menos usado — restaurante com
 * milhares de produtos não pode segurar todas as imagens na memória do tablet.</p>
 */
@Pipe({
  name: 'authimage'
})
export class AuthImagePipe implements PipeTransform {

  /** Teto de imagens guardadas. Além disso, descarta a menos usada e revoga o object URL. */
  private static readonly LIMITE = 300;

  /** src -> object URL já resolvido. Map preserva ordem de inserção, que é o que o LRU usa. */
  private static readonly cache = new Map<string, string>();

  /** src -> promessa em andamento, para dois tiles do mesmo produto não gerarem dois downloads. */
  private static readonly emVoo = new Map<string, Promise<string>>();

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    public jwtAuthService: JwtAuthService
  ) { }

  transform(src: string): Promise<SafeUrl | string> {
    if (!src) {
      return Promise.resolve('./assets/images/no-image.jpeg');
    }
    const doCache = AuthImagePipe.cache.get(src);
    if (doCache) {
      // reinsere para marcar como recém-usada (LRU pela ordem do Map).
      AuthImagePipe.cache.delete(src);
      AuthImagePipe.cache.set(src, doCache);
      return Promise.resolve(this.liberar(doCache));
    }
    const jaPedida = AuthImagePipe.emVoo.get(src);
    if (jaPedida) {
      return jaPedida.then(url => url.startsWith('blob:') ? this.liberar(url) : url);
    }

    const token = this.jwtAuthService.getJwtTokenByUrl(src);
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    const promessa = this.http.get(src, { headers, responseType: 'blob' }).toPromise()
      .then(blob => {
        const url = URL.createObjectURL(blob);
        AuthImagePipe.guardar(src, url);
        return url;
      })
      .catch(() => './assets/images/no-image.jpeg')
      .finally(() => AuthImagePipe.emVoo.delete(src));

    AuthImagePipe.emVoo.set(src, promessa);
    return promessa.then(url => url.startsWith('blob:') ? this.liberar(url) : url);
  }

  /**
   * Libera a object URL para o binding. Sem isto o Angular a bloqueia como esquema desconhecido.
   * É seguro: a URL foi criada aqui mesmo, a partir de um blob que a própria aplicação baixou.
   */
  private liberar(url: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  private static guardar(src: string, url: string): void {
    AuthImagePipe.cache.set(src, url);
    while (AuthImagePipe.cache.size > AuthImagePipe.LIMITE) {
      const maisAntiga = AuthImagePipe.cache.keys().next();
      if (maisAntiga.done) {
        break;
      }
      const urlDescartada = AuthImagePipe.cache.get(maisAntiga.value);
      AuthImagePipe.cache.delete(maisAntiga.value);
      // sem revoke o blob fica na memória do navegador até a aba fechar.
      if (urlDescartada) {
        URL.revokeObjectURL(urlDescartada);
      }
    }
  }

  /** Limpa tudo — usar depois de trocar a foto de um produto, ou ao sair da empresa. */
  static limpar(): void {
    AuthImagePipe.cache.forEach(url => URL.revokeObjectURL(url));
    AuthImagePipe.cache.clear();
    AuthImagePipe.emVoo.clear();
  }

}
